import {
	type CurrencyEnum,
	type PreviewOrderQuoteOfferFeasibilityInput,
	OrderShippingMethod,
} from '@codezo/graphql';
import { useMemo } from 'react';
import { Layers, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { CurrencyInput } from '../../ui/currency-input';
import { NumberInput } from '../../ui/number-input';
import { fieldSurface } from '../../ui/field-surface';
import { FeasibilityBadge } from '../../ui/feasibility-badge';
import { useFeasibilityPreview } from '../../../hooks/useFeasibilityPreview';
import { filled } from './delivery-rules';
import { cn } from '../../../lib/utils';
import { SHIPPING_METHOD_LABEL } from '../../../lib/status-configs';
import {
	type SplitDraft,
	type SplitLegDraft,
	emptySplit,
	isSplitBlank,
	legQuantities,
	splitIncompleteReason,
	splitSummary,
} from './split-rules';

const methodLabel = (m: OrderShippingMethod) => SHIPPING_METHOD_LABEL[m];

/**
 * A labelled field, the same pairing the method cards above use.
 *
 * The width is fixed by the caller rather than left to the content: the legs
 * stack, so a slot that sizes itself puts the same label at two different left
 * edges on the two rows.
 */
function Field({
	label,
	width,
	children,
}: {
	label: string;
	width: string;
	children: React.ReactNode;
}) {
	return (
		<div className={cn('flex flex-col gap-1', width)}>
			<span className="text-2xs text-text-4">{label}</span>
			{children}
		</div>
	);
}

/** The small square that marks a method, matching the method cards above. */
function MethodDot({ method }: { method: OrderShippingMethod }) {
	return (
		<span
			aria-hidden
			className={cn(
				'size-1.5 shrink-0 rounded-[1px]',
				method === OrderShippingMethod.Express ? 'bg-[var(--accent)]' : 'bg-text-3'
			)}
		/>
	);
}

/**
 * The splits on one quantity tier, under the two methods they combine.
 *
 * Optional extras, and drawn as such: nothing until one exists, just the action
 * that makes one. The two methods above are what every tier must have; a split
 * is a third option offered on top of them.
 */
export function SplitsSection({
	splits,
	tierQuantity,
	currency,
	deadline,
	acceptDeadline,
	production,
	onChange,
}: {
	splits: SplitDraft[];
	tierQuantity: number;
	currency: CurrencyEnum;
	deadline: string | null;
	acceptDeadline: string | null;
	/** The tier's production window — every leg waits on the same run. */
	production: { min?: number | null; max?: number | null };
	onChange: (next: SplitDraft[]) => void;
}) {
	return (
		<div className="flex flex-col gap-2">
			{splits.map((split, index) => (
				<SplitCard
					key={split.existingId ?? `draft-${index}`}
					split={split}
					tierQuantity={tierQuantity}
					currency={currency}
					deadline={deadline}
					acceptDeadline={acceptDeadline}
					production={production}
					onPatch={(patch) =>
						onChange(splits.map((s, i) => (i === index ? { ...s, ...patch } : s)))
					}
					onRemove={() => onChange(splits.filter((_, i) => i !== index))}
				/>
			))}

			<div>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={() => onChange([...splits, emptySplit()])}
				>
					<Plus size={12} />
					Add split
				</Button>
			</div>
		</div>
	);
}

function SplitCard({
	split,
	tierQuantity,
	currency,
	deadline,
	acceptDeadline,
	production,
	onPatch,
	onRemove,
}: {
	split: SplitDraft;
	tierQuantity: number;
	currency: CurrencyEnum;
	deadline: string | null;
	acceptDeadline: string | null;
	production: { min?: number | null; max?: number | null };
	onPatch: (patch: Partial<SplitDraft>) => void;
	onRemove: () => void;
}) {
	const quantities = legQuantities(split, tierQuantity);
	const reason = splitIncompleteReason(split, tierQuantity);
	const blank = isSplitBlank(split);
	/* A saved split the tier outgrew. Stated before anything else: until the legs
	   are redone the option is offered to nobody. */
	const stale = split.serverValid === false;

	const total = split.legs.reduce((sum, leg) => {
		const n = parseFloat(leg.price);
		return sum + (Number.isFinite(n) ? n : 0);
	}, 0);

	const patchLeg = (key: string, patch: Partial<SplitLegDraft>) =>
		onPatch({ legs: split.legs.map((leg) => (leg.key === key ? { ...leg, ...patch } : leg)) });

	/* The split's own preview, not the tier's.
	 *
	 * A leg's days are its own, so the method cards' arrivals do not describe it.
	 * The result comes back keyed by shipping method, which is exactly why this
	 * has to be a separate call rather than extra entries in the tier's: a leg
	 * and its namesake method would collide there and one would silently
	 * overwrite the other. Within one split the legs are distinct methods —
	 * Express ahead, Standard after — so keying by method is safe here. */
	const feasInput = useMemo((): PreviewOrderQuoteOfferFeasibilityInput | null => {
		if (production.min == null) return null;
		return {
			minProductionDays: production.min,
			maxProductionDays: production.max ?? null,
			deadline: deadline ?? undefined,
			acceptDeadline: acceptDeadline ?? undefined,
			deliveries: split.legs.flatMap((leg) =>
				filled(leg.deliveryDaysMin) && filled(leg.deliveryDaysMax)
					? [
							{
								shippingMethod: leg.method,
								deliveryDaysMin: parseInt(leg.deliveryDaysMin, 10),
								deliveryDaysMax: parseInt(leg.deliveryDaysMax, 10),
							},
						]
					: []
			),
		};
	}, [production, deadline, acceptDeadline, split.legs]);

	const { byMethod } = useFeasibilityPreview(feasInput);

	return (
		<div
			className={cn(
				'rounded-lg border',
				stale ? 'border-status-warn bg-status-warn-soft' : 'border-line-2 bg-surface-fill'
			)}
		>
			<div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
				<Layers size={12} className="shrink-0 text-[var(--accent)]" />
				<span className="shrink-0 text-xs font-medium text-text-1">Split</span>
				<span className="min-w-0 flex-1 truncate text-xs text-text-3">
					{blank
						? 'Divide the tier between the two methods'
						: splitSummary(split, tierQuantity, methodLabel)}
				</span>
				{!blank && total > 0 && (
					<span className="shrink-0 font-mono text-xs text-text-2">
						Total {formatMoney(total, currency)}
					</span>
				)}
				<button
					type="button"
					onClick={onRemove}
					aria-label="Remove this split"
					className="flex size-6 shrink-0 items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-text-1"
				>
					<Trash2 size={12} />
				</button>
			</div>

			{stale && (
				<p className="px-3 pb-2 text-2xs text-status-warn">
					The tier quantity changed, so the client is not being offered this split.
					Re-enter the parts to bring it back.
				</p>
			)}

			<div className="border-t border-line-1">
				{split.legs.map((leg, i) => {
					const last = i === split.legs.length - 1;
					const quantity = quantities[i] ?? 0;
					const result = byMethod.get(leg.method) ?? null;
					return (
						<div
							key={leg.key}
							className={cn('px-3 py-2.5', i > 0 && 'border-t border-line-1')}
						>
							{/* The leg's identity and its verdict on one line, the fields
							    it is built from on the next. They were one row of five
							    input columns, which left every field at its floor: a
							    five-figure price clipped inside its own box and the
							    arrival badge wrapped onto two lines. Nothing here is
							    optional, so the answer is a second line rather than a
							    narrower field. */}
							<div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
								{/* Stated, not chosen. A split exists to send part of the
								    tier ahead by the faster method and the rest by the
								    slower one — that IS the option, so there is no pick
								    to offer: swapping them would describe the same two
								    shipments in the other order. */}
								<MethodDot method={leg.method} />
								<span className="text-sm font-medium text-text-1">
									{methodLabel(leg.method)}
								</span>
								<span className="ml-auto flex min-w-0 items-center">
									{result ? (
										<FeasibilityBadge
											verdict={result.verdict}
											projected={result.projectedDelivery}
											deadline={deadline}
											breakdown={result.breakdown}
											days={{
												productionMin: production.min,
												productionMax: production.max,
												transitMin: Number(leg.deliveryDaysMin),
												transitMax: Number(leg.deliveryDaysMax),
											}}
										/>
									) : (
										<span className="text-xs text-text-4">
											No lead time yet
										</span>
									)}
								</span>
							</div>

							{/* Fixed widths, not fractions: the two legs sit one above the
							    other and their fields have to line up, which a grid of
							    fr tracks would only manage while both rows held the same
							    controls — and the last leg has no quantity input. */}
							<div className="flex flex-wrap items-end gap-x-4 gap-y-2">
								{/* Every leg keeps this slot, the last one filled with what
								    the others left rather than an input. Omitting it there
								    pulled that leg's Price and Days left into the columns
								    the leg above uses for something else, and the two rows
								    stopped lining up — which is the whole reason these
								    widths are fixed. */}
								<Field
									label={last ? 'Quantity · the rest' : 'Quantity'}
									width="w-32"
								>
									{last ? (
										/* A locked field, not bare text. It holds the same
										   kind of value as the input above it and belongs
										   in the same kind of box; as loose text it read as
										   a caption and left the row looking unfinished.
										 *
										 * Built from the field recipe rather than a disabled
										   NumberInput: that component's steppers act on its
										   ref, so they would still drive a value nothing is
										   listening to. */
										<span className="flex items-center gap-1.5">
											<span
												aria-label="Quantity travelling on the last part"
												title="What the parts above it leave"
												className={cn(
													fieldSurface,
													'flex h-8 w-24 cursor-not-allowed items-center px-2.5 opacity-60'
												)}
											>
												<span className="truncate font-mono text-sm text-text-2">
													{quantity > 0 ? quantity.toLocaleString() : '—'}
												</span>
											</span>
											<span className="shrink-0 text-xs text-text-4">
												pcs
											</span>
										</span>
									) : (
										<span className="flex items-center gap-1.5">
											<div className="w-24">
												<NumberInput
													value={leg.quantity}
													onChange={(e) =>
														patchLeg(leg.key, {
															quantity: (e.target as HTMLInputElement)
																.value,
														})
													}
													placeholder="—"
													aria-label={`Quantity travelling ${methodLabel(leg.method)}`}
													min={1}
												/>
											</div>
											<span className="shrink-0 text-xs text-text-4">
												pcs
											</span>
										</span>
									)}
								</Field>

								<Field label="Price" width="w-40">
									<div className="w-40">
										<CurrencyInput
											currency={currency}
											value={leg.price}
											onChange={(e) =>
												patchLeg(leg.key, {
													price: (e.target as HTMLInputElement).value,
												})
											}
											placeholder="0.00"
											aria-label={`Price for the ${methodLabel(leg.method)} part`}
											min={0}
											step="any"
										/>
									</div>
								</Field>

								{/* The leg's own, not its method's. A tier may offer only
								    Standard on its own and still carry a split with an
								    Express half — there is then no method card to take a
								    lead time from. */}
								<Field label="Days" width="w-[184px]">
									<span className="flex items-center gap-1.5">
										<div className="w-20">
											<NumberInput
												value={leg.deliveryDaysMin}
												onChange={(e) =>
													patchLeg(leg.key, {
														deliveryDaysMin: (
															e.target as HTMLInputElement
														).value,
													})
												}
												placeholder="—"
												aria-label={`Minimum transit days for the ${methodLabel(leg.method)} part`}
												min={0}
											/>
										</div>
										<span className="text-xs text-text-4">–</span>
										<div className="w-20">
											<NumberInput
												value={leg.deliveryDaysMax}
												onChange={(e) =>
													patchLeg(leg.key, {
														deliveryDaysMax: (
															e.target as HTMLInputElement
														).value,
													})
												}
												placeholder="—"
												aria-label={`Maximum transit days for the ${methodLabel(leg.method)} part`}
												min={0}
											/>
										</div>
									</span>
								</Field>
							</div>
						</div>
					);
				})}

				{reason && (
					<div className="border-t border-line-1 px-3 py-1.5">
						<span className="text-2xs text-status-warn">{reason}</span>
					</div>
				)}
			</div>
		</div>
	);
}

function formatMoney(amount: number, currency: CurrencyEnum) {
	try {
		return new Intl.NumberFormat(undefined, {
			style: 'currency',
			currency,
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		/* An unknown code is not a reason to blank the only total on the card. */
		return `${currency} ${amount.toFixed(2)}`;
	}
}
